import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { ArrowLeft, FileSpreadsheet, Loader2, RotateCcw, ShieldCheck } from "lucide-react";
import {
  validateTranslation,
  type ValidationFinding,
  type ValidationReport,
  type ValidationSeverity,
} from "@/lib/api";
import { downloadValidationCsv } from "@/lib/exports";
import { clearWorkflow, getLastIr, getLastTranslation } from "@/lib/workflow";

export function ValidatePage() {
  const navigate = useNavigate();
  const [ir] = useState(getLastIr());
  const [translation] = useState(getLastTranslation());

  const mutation = useMutation<ValidationReport, Error>({
    mutationFn: () => validateTranslation(ir!, translation),
  });

  function startOver() {
    clearWorkflow();
    navigate("/parse");
  }

  if (!ir) {
    return (
      <div className="mx-auto max-w-3xl px-6 py-12">
        <div className="rounded-xl2 border border-dashed border-bridge-slate/30 bg-white p-8 text-center">
          <p className="text-sm font-medium text-bridge-ink">No parsed IR in this session.</p>
          <p className="mt-1 text-2xs text-bridge-slate">
            Visit the Parse page first to ingest a Cognos report.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-5xl px-6 py-8">
      <header className="mb-6">
        <h1 className="font-display text-2xl font-semibold text-bridge-ink">
          Validate — {ir.displayName}
        </h1>
        <p className="mt-0.5 text-sm text-bridge-slate">
          {translation
            ? "Post-translation diff — ERROR findings indicate the translation should not ship."
            : "Pre-translation source check — run Translate first for the post-translation diff."}
        </p>
      </header>

      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => navigate(translation ? "/translate" : "/parse")}
          className="inline-flex items-center gap-1 rounded-md border border-bridge-slate/30 bg-white px-3 py-1.5 text-xs font-medium text-bridge-ink hover:bg-bridge-mist"
        >
          <ArrowLeft size={12} /> Back to {translation ? "translate" : "parse"}
        </button>
        <button
          onClick={() => mutation.mutate()}
          disabled={mutation.isPending}
          className="inline-flex items-center gap-1 rounded-md bg-bridge-primary px-3 py-1.5 text-xs font-medium text-white shadow-sm hover:bg-bridge-primaryDark disabled:opacity-40"
        >
          {mutation.isPending
            ? <Loader2 size={12} className="animate-spin" />
            : <ShieldCheck size={12} />}
          Run validation
        </button>
        {mutation.data && mutation.data.findings.length > 0 && (
          <button
            type="button"
            onClick={() => downloadValidationCsv(mutation.data!)}
            title="Download findings as CSV — one row per finding."
            className="inline-flex items-center gap-1 rounded-md border border-bridge-slate/30 bg-white px-3 py-1.5 text-xs font-medium text-bridge-ink hover:bg-bridge-mist"
          >
            <FileSpreadsheet size={12} /> Download CSV
          </button>
        )}
        {mutation.data && (
          <button
            type="button"
            onClick={startOver}
            title="Clear the current IR + translation and return to the Parse step"
            className="ml-auto inline-flex items-center gap-1 rounded-md border border-bridge-slate/30 bg-white px-3 py-1.5 text-xs font-medium text-bridge-ink hover:bg-bridge-mist"
          >
            <RotateCcw size={12} /> Start over
          </button>
        )}
      </div>

      {mutation.error && (
        <p className="mt-4 rounded-md border border-bridge-danger/30 bg-bridge-danger/5 p-3 text-sm text-bridge-danger">
          Validate failed — {mutation.error.message}
        </p>
      )}
      {mutation.data && <ValidationView report={mutation.data} />}
    </div>
  );
}

function ValidationView({ report }: { report: ValidationReport }) {
  const errors = report.countsBySeverity.ERROR ?? 0;
  const warns = report.countsBySeverity.WARNING ?? 0;
  const infos = report.countsBySeverity.INFO ?? 0;

  return (
    <section className="mt-4 rounded-xl2 border border-bridge-slate/20 bg-white p-4 shadow-card">
      <div data-tour-id="validate-summary" className="flex items-center gap-3">
        <CountTile label="errors" count={errors} severity="ERROR" />
        <CountTile label="warnings" count={warns} severity="WARNING" />
        <CountTile label="info" count={infos} severity="INFO" />
      </div>
      {report.findings.length === 0 && (
        <p className="mt-4 text-sm text-bridge-slate">No findings.</p>
      )}
      {report.findings.length > 0 && (
        <ul data-tour-id="validate-details" className="mt-4 space-y-2 text-xs">
          {report.findings.map((f, i) => <FindingRow key={i} finding={f} />)}
        </ul>
      )}
    </section>
  );
}

function CountTile({
  label,
  count,
  severity,
}: {
  label: string;
  count: number;
  severity: ValidationSeverity;
}) {
  const cls = severity === "ERROR"
    ? "border-bridge-danger/30 bg-bridge-danger/5 text-bridge-danger"
    : severity === "WARNING"
      ? "border-bridge-warn/30 bg-bridge-warn/5 text-bridge-warn"
      : "border-bridge-slate/30 bg-bridge-mist text-bridge-ink";
  return (
    <div className={`rounded-md border px-3 py-2 ${cls}`}>
      <div className="text-2xs uppercase tracking-wide">{label}</div>
      <div className="font-mono text-base">{count}</div>
    </div>
  );
}

function FindingRow({ finding }: { finding: ValidationFinding }) {
  const cls = finding.severity === "ERROR"
    ? "border-bridge-danger/30 bg-bridge-danger/5"
    : finding.severity === "WARNING"
      ? "border-bridge-warn/30 bg-bridge-warn/5"
      : "border-bridge-slate/25 bg-white";
  return (
    <li className={`rounded-md border p-2 ${cls}`}>
      <div className="flex items-center gap-2 text-2xs">
        <span className="font-mono font-semibold text-bridge-ink">{finding.code}</span>
        <span className="text-bridge-slate">{finding.severity}</span>
        <span className="ml-auto text-bridge-slate">{finding.location}</span>
      </div>
      <p className="mt-1 text-xs text-bridge-ink/90">{finding.message}</p>
    </li>
  );
}
